import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface AuthUser {
  id: number;
  username?: string;
  email?: string;
  displayName?: string;
}

export interface AuthState {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
}

const AUTH_STORAGE_KEY = 'auth';

const hydrateAuthUser = (user: any): AuthUser | null => {
  if (!user || typeof user !== 'object') return null;

  const rawId = user.user_id || user.id || user.ID;
  const normalizedId = Number(rawId);

  return {
    id: !isNaN(normalizedId) ? normalizedId : 0,
    username: user.username || user.user_login || 'User',
    email: user.email || user.user_email || '',
    displayName: user.displayName || user.display_name || user.username || '',
  };
};

const loadAuthState = (): AuthState => {
  if (typeof window === 'undefined') return { user: null, token: null, isAuthenticated: false };
  const persisted = window.localStorage.getItem(AUTH_STORAGE_KEY);
  if (!persisted) return { user: null, token: null, isAuthenticated: false };

  try {
    const parsed = JSON.parse(persisted);
    const token = parsed.token || null;
    const user = hydrateAuthUser(parsed.user);
    
    return { user, token, isAuthenticated: Boolean(token) };
  } catch {
    return { user: null, token: null, isAuthenticated: false };
  }
};

const persistAuthState = (token: string | null, user: AuthUser | null): void => {
  if (typeof window === 'undefined') return;
  if (!token) {
    window.localStorage.removeItem(AUTH_STORAGE_KEY);
    return;
  }
  window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({ token, user }));
};

const initialState: AuthState = loadAuthState();

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginSuccess(state, action: PayloadAction<{ token: string; user: any }>) {
      // console.log("FULL USER OBJECT FROM API:", action.payload.user);
      state.token = action.payload.token;
      state.user = hydrateAuthUser(action.payload.user);
      state.isAuthenticated = true; 
      persistAuthState(state.token, state.user);
    },
    logout(state) {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      persistAuthState(null, null);
    },
  },
});

export const { loginSuccess, logout } = authSlice.actions;
export default authSlice.reducer;